/**
 * Backend reachability tracker (Rhys, 2026-08-31): a dev server killed hours
 * ago used to fail silently — the page stayed up, every click died quietly,
 * and staff hunted around wondering why nothing loads. This module turns that
 * into one clear signal the BackendDownBanner renders.
 *
 * How "down" is decided:
 * - Any axios call to OUR API that dies with a network error (no response at
 *   all) marks the backend down. Timeouts and canceled requests do not —
 *   slow is not dead, and aborts are routine.
 * - While down, /api/health is polled every few seconds; the first success
 *   (or any successful API response) clears the state.
 * - On localhost only, a heartbeat also probes on load, when the tab becomes
 *   visible again, and once a minute — so the banner is already up when you
 *   come back to a tab whose server died, before you click anything.
 */

import axios from 'axios';
import { getApiBaseUrl, isLocalDevHost } from './apiClient';

const DOWN_POLL_MS = 4000;
const HEARTBEAT_MS = 60000;
const PROBE_TIMEOUT_MS = 4000;

let backendDown = false;
let downSince = null;
let pollTimer = null;
let heartbeatTimer = null;
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => {
    try {
      listener(backendDown);
    } catch {
      /* a broken listener must not break the rest */
    }
  });
}

export function isBackendDown() {
  return backendDown;
}

export function getBackendDownSince() {
  return downSince;
}

/** Subscribe to up/down changes. Returns an unsubscribe function. */
export function subscribeBackendHealth(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setDown(nextDown) {
  if (backendDown === nextDown) return;
  backendDown = nextDown;
  downSince = nextDown ? new Date().toISOString() : null;
  if (nextDown) startDownPolling();
  else stopDownPolling();
  notify();
}

/** Was this failed/succeeded request aimed at our own backend? */
function isOwnApiRequest(config) {
  const url = String(config?.url || '');
  if (!url) return false;
  if (url.startsWith('/api/')) return true;
  const base = getApiBaseUrl();
  if (base && url.startsWith(base)) return true;
  return false;
}

/** No response at all, and not a timeout or a deliberate cancel. */
function isHardNetworkError(error) {
  if (!error || error.response) return false;
  if (error.code === 'ECONNABORTED') return false;
  if (error.code === 'ERR_CANCELED' || (axios.isCancel && axios.isCancel(error))) return false;
  return error.code === 'ERR_NETWORK' || error.message === 'Network Error';
}

/** Called by the global axios interceptor for every failed request. */
export function reportApiError(error) {
  if (!isHardNetworkError(error)) return;
  if (!isOwnApiRequest(error.config)) return;
  setDown(true);
}

/** Called by the global axios interceptor for every successful response. */
export function reportApiSuccess(response) {
  if (!backendDown) return;
  if (!isOwnApiRequest(response?.config)) return;
  setDown(false);
}

/** One /api/health probe; flips the state to match the outcome. */
export async function probeBackend() {
  try {
    await axios.get(`${getApiBaseUrl()}/api/health`, {
      timeout: PROBE_TIMEOUT_MS,
      withCredentials: true
    });
    setDown(false);
    return true;
  } catch (error) {
    // Only a hard network failure means down — a 4xx/5xx answer is still a
    // server that is up and talking.
    if (isHardNetworkError(error)) setDown(true);
    else setDown(false);
    return !backendDown;
  }
}

function startDownPolling() {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    probeBackend();
  }, DOWN_POLL_MS);
}

function stopDownPolling() {
  if (!pollTimer) return;
  clearInterval(pollTimer);
  pollTimer = null;
}

/**
 * Localhost-only heartbeat: probe now, when the tab is fronted again, and
 * once a minute while visible. Production stays passive (failed real requests
 * plus the down-poller) so warehouse tablets add no background load.
 */
export function startLocalhostHeartbeat() {
  if (!isLocalDevHost() || heartbeatTimer) return;
  probeBackend();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') probeBackend();
  });
  heartbeatTimer = setInterval(() => {
    if (document.visibilityState === 'visible' && !backendDown) probeBackend();
  }, HEARTBEAT_MS);
}
